import {invokeApi} from "../bl_libs/invokeApi";
import {use_e2ee} from "../utils/utils";
import {LOCAL_STORAGE_KEYS} from "../utils/constants";
import { extractOrgSectionsTableData, isOrgSectionRow } from "../utils/orgSectionsNormalize";

export const createClientService = async (org_details) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/client/create',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    };

    requestObj['postData'] = {
        ...org_details,
    }

    return invokeApi(requestObj, use_e2ee());
};

export const editClientService = async (org_code, org_details) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/client/edit',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    };

    requestObj['postData'] = {
        org_code,
        ...org_details,
    }

    return invokeApi(requestObj, use_e2ee());
};

/**
 * Publish a client workspace: take it out of Draft, and send its contact the
 * email that lets them sign in. Idempotent on the server — publishing an
 * already-published workspace returns `already_published` and sends nothing.
 *
 * No parental-consent pathway: since 5 Oct 2026 the school or network sets up
 * its own method from its own workspace (getConsentSetupService /
 * confirmConsentPathwayService below), not the partner at publish.
 */
/*
 * `institute_code` names WHICH school, and is required whenever a network
 * publishes one of its own.
 *
 * Schools of a network share the network's org_code, so org_code alone
 * arrives at the server looking like the caller publishing itself — which is
 * refused, because an account is published by its partner and never by
 * itself. The institute is what distinguishes "this school of mine" from
 * "me". Omitted for a standalone school, which owns its code outright.
 */
export const publishClientService = async (org_code, { institute_code } = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/publish',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, institute_code },
        },
        use_e2ee(),
    );
};

/**
 * The signed-in school's or network's own parental-consent method.
 *
 * Resolves to `{ consent_pathway, assigned_pathway, network, needs_setup,
 * is_school_network, org_name, ... }`. `consent_pathway` is the CONFIRMED method
 * (null until set up); `assigned_pathway` is the network's method, pre-selected
 * for a school inside a network until that school confirms its own.
 */
export const getConsentSetupService = async () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/consent/setup',
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
        },
        use_e2ee(),
    );
};

/**
 * Confirm the method. Once only — the server refuses a second confirm (409),
 * because learners admitted under one consent basis can't be moved to another.
 *
 * @param {number} consent_pathway  ACCOUNT_CONSENT_PATHWAY
 */
export const confirmConsentPathwayService = async (consent_pathway) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/consent/confirm',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { consent_pathway },
        },
        use_e2ee(),
    );
};

/**
 * Publish ONE institute — a school that has no Organization of its own.
 *
 * Two kinds of "school" live under a workspace, and they publish differently:
 *
 *   · A school WITH its own workspace — its own login, licences and contact —
 *     is an Organization. `publishClientService` publishes that, and mails its
 *     contact their sign-in details.
 *   · A school WITHOUT one is only an OrgInstitute row, created by the CSV
 *     import from an `institute_code` in the upload head. Nobody signs in as it,
 *     so publishing it just marks it live. These are the rows
 *     `listOrgSchoolsService` returns.
 *
 * The server refuses the wrong one: pass an institute_code that DOES have an
 * Organization and it returns an error pointing at the account publish instead,
 * rather than listing a live school whose contact was never invited. So a caller
 * cannot silently activate a school nobody was told about.
 *
 * No consent pathway here, deliberately. The pathway is the basis on which an
 * ACCOUNT admits learners, and that decision belongs to the Organization
 * publish; an institute inherits whatever its workspace already chose.
 *
 * @param {string} org_code       owning workspace; omit to act as your own
 * @param {string} institute_code the school to publish
 */
export const publishInstituteService = async (org_code, institute_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/institute/publish',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, institute_code },
        },
        use_e2ee(),
    );
};

/**
 * Re-send the activation email for an ALREADY published workspace. Changes no
 * status and consumes no licence — for when the first email went astray.
 */
export const resendClientActivationService = async (org_code, institute_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/resend_activation',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, institute_code },
        },
        use_e2ee(),
    );
};

/**
 * Everything the "Who gets a learning space?" dialog renders: the scope options
 * with their student counts, `classes[]` and `grades[]` (per-class / per-grade
 * counts for the multi-selects), and the org's licence position. Read-only —
 * spends nothing, sends nothing.
 *
 * `section_code` is optional (the toolbar sends none). `institute_code` narrows
 * a network to one of its schools.
 */
export const learnerSpacePreviewService = async (org_code, section_code = null, institute_code = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/section/publish_preview',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: {
                org_code,
                ...(section_code ? { section_code } : {}),
                ...(institute_code ? { institute_code } : {}),
            },
        },
        use_e2ee(),
    );
};

/**
 * Publish the learner spaces for a set of classes: spends a seat per
 * unactivated student, sends their sign-in details, marks the classes live.
 *
 * Only `section_codes` is sent — never counts. The server recounts everything
 * that decides the charge, because the roster can change while the dialog is
 * open. An insufficient pool comes back as an error whose `data` carries
 * `{ shortfall, students_selected, licenses_available }`.
 */
export const publishLearnerSpacesService = async (org_code, section_codes) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/section/publish',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, section_codes },
        },
        use_e2ee(),
    );
};

/*
 * `institute_code` names WHICH school inside the workspace.
 *
 * Schools of a network share the network's org_code, so org_code alone
 * resolves to the account that OWNS that code — the network — and the
 * licences land there rather than on the school the user picked. Omitted for
 * a standalone school or the network itself, both of which own their code.
 */
export const assignLicensesService = async (org_code, license_count, institute_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/client/assign_licenses',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    };

    requestObj['postData'] = {
        org_code,
        license_count,
        institute_code
    }

    return invokeApi(requestObj, use_e2ee());
};

/*
 * DQ Pro partner plans: assign licences and credits to a network or school,
 * converting DQ Tokens live.
 * params / data: { org_code, institute_code?, licences?, credits? }
 */

// GET — the live quote: DQT cost, balances before/after, allowed + reason. Moves nothing.
export const quoteAssignmentService = (params) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/client/assignment/quote',
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        queryParams: params,
    }, use_e2ee());
};

// POST — assign. Returns the same shape as the quote, with the balances as they now are.
export const assignToOrgService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/client/assignment/assign',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: data,
    }, use_e2ee());
};

//Assign Licenses
export const assignSectionLicensesService = async (org_code, section_code, license_count) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/section/assign_licenses',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, section_code, license_count },
        },
        use_e2ee(),
    );
};


/**
 * `institute_code` names one school INSIDE a network — schools there share the
 * network's org_code. With it, the network makes the item available to that
 * school only (partner -> network -> school -> class). Omit it for a network or
 * a standalone school.
 */
export const linkAssessmentService = async (org_code, dq_module_codes, visibility_scope, institute_code = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/client/link_assessment',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    };

    requestObj['postData'] = {
        org_code,
        dq_module_codes,
    };
    if (visibility_scope) {
        requestObj.postData.visibility_scope = visibility_scope;
    }
    if (institute_code) {
        requestObj.postData.institute_code = institute_code;
    }

    return invokeApi(requestObj, use_e2ee());
};

/** What is linked to `org_code` — or, with `institute_code`, to that one school of a network. */
export const getLinkedAssessmentService = async (org_code, institute_code = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const q = institute_code ? `?${new URLSearchParams({ institute_code }).toString()}` : '';
    let requestObj = {
        path: `/dq_insider/dqin/client/get_linked_assessment/${org_code}${q}`,
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    return invokeApi(requestObj, use_e2ee());
};

/**
 * @param {'all'|'single'|'networks'} [account_filter]  the pill on the school
 *   list (PW deck slide 33). Applied server-side — filtering the fetched page
 *   would only ever see `limit` rows, so the counts and paging would be wrong.
 */
export const listClientsService = async ({ search = '', page = 1, limit = 5, account_filter = 'all' } = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/client/list',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: { search: search || '', page, limit, account_filter },
    };
    return invokeApi(requestObj, use_e2ee());
};

export const selectClientPlanService = (plan_key) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/plan/select',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
    };

    requestObj['postData'] = {
        plan_key
    }

    return invokeApi(requestObj);
};

export const createPeerService = (peer_details) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/peer/create',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { ...peer_details },
    };
    return invokeApi(requestObj);
};

export const editPeerService = (peer_details) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/peer/edit',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { ...peer_details },
    };
    return invokeApi(requestObj);
};

export const deletePeerService = (email) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/peer/delete',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { email },
    };
    return invokeApi(requestObj);
};

export const listPeersService = ({ search = '', page = 1, limit = 10 } = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const params = new URLSearchParams({ search, page, limit }).toString();
    const requestObj = {
        path: `/dq_insider/dqin/client/peer/list?${params}`,
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    };
    return invokeApi(requestObj);
};

// ── Unlock requests (School / School Network → its Partner) ─────────────────────

/**
 * School / network → what its "Unlock a capability" dialog shows: the three
 * SOLUTION UPGRADES with state active | requested | available, read from the
 * org's own product access, and the "Current access" line.
 */
export const getUnlockOptionsService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/client/unlock-request/options',
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    });
};

/**
 * School / network → ask its partner to unlock a capability. The request
 * appears in the partner's Opportunity Pipeline; the partner is emailed and
 * the requester gets a receipt.
 *
 * @param {string} requested_capability enterprise_p0 | enterprise_lc | enterprise_p1
 */
export const createUnlockRequestService = (requested_capability) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/client/unlock-request/create',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { requested_capability },
    });
};

/**
 * Partner → decide a school's or network's unlock request.
 *
 * @param {object} p
 * @param {string} p.request_id
 * @param {'accept'|'approve'|'decline'} p.decision
 * @param {string} [p.decline_reason]  required for decline
 * @param {string} [p.variant]  COS Readiness only: enterprise_p1a | enterprise_p1b
 */
export const decideUnlockRequestService = ({ request_id, decision, decline_reason, variant }) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const postData = { request_id, decision };
    if (decline_reason) postData.decline_reason = decline_reason;
    if (variant) postData.variant = variant;
    return invokeApi({
        path: '/dq_insider/dqin/client/unlock-request/decide',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData,
    });
};

/**
 * School / network → ask its partner for the learner licences a class publish
 * is short of. Sends the classes, never a count: the server works out each
 * school's shortfall and raises one request per short school. The partner is
 * emailed and the requester gets a receipt.
 *
 * @param {object} p
 * @param {string} p.org_code
 * @param {string[]} p.section_codes   the classes the publish was for
 * @param {string} [p.institute_code]  a network acting for one of its schools
 */
export const createLicenceRequestService = ({ org_code, section_codes, institute_code }) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const postData = { org_code, section_codes };
    if (institute_code) postData.institute_code = institute_code;
    return invokeApi({
        path: '/dq_insider/dqin/client/licence-request/create',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData,
    }, use_e2ee());
};

/**
 * Partner → what approving a licence request would cost. Moves nothing.
 * data: { uses_dqt, licences, quote } — quote is null on a legacy plan,
 * where licences are assigned free.
 */
export const quoteLicenceRequestService = ({ request_id, licences }) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const queryParams = { request_id };
    if (licences) queryParams.licences = licences;
    return invokeApi({
        path: '/dq_insider/dqin/client/licence-request/quote',
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        queryParams,
    }, use_e2ee());
};

/**
 * Partner → decide a school's or network's licence request.
 *
 * @param {object} p
 * @param {string} p.request_id
 * @param {'approve'|'decline'} p.decision
 * @param {number} [p.licences]        approve: how many to assign (defaults to the ask)
 * @param {string} [p.decline_reason]  required for decline
 */
export const decideLicenceRequestService = ({ request_id, decision, licences, decline_reason }) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const postData = { request_id, decision };
    if (licences) postData.licences = licences;
    if (decline_reason) postData.decline_reason = decline_reason;
    return invokeApi({
        path: '/dq_insider/dqin/client/licence-request/decide',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData,
    }, use_e2ee());
};

/**
 * Partner → ask DQ for something the workspace does not hold: any partner
 * request type (a solution upgrade, a dashboard, the COS Government
 * Checklist). The DQ admins are emailed and decide it in the admin portal.
 *
 * @param {object} p
 * @param {string} p.request_type  enterprise_p0 | enterprise_lc | enterprise_p1a |
 *   enterprise_p1b | network_dashboard | national_dashboard | government_checklist
 * @param {object} [p.details]     { field_key: value }, the type's fields
 * @param {string} [p.source]      unlock_dialog | solution_overview | solution_preview
 */
export const createPartnerRequestService = ({ request_type, details = {}, source }) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/workspace/partner-request/create',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { request_type, details, ...(source ? { source } : {}) },
    }, use_e2ee());
};

/**
 * Partner → every request type it can send DQ, each with its fields and where
 * the partner's latest one stands (available | requested | approved), plus
 * form prefill. can_request is false for anyone but the workspace owner.
 */
export const getPartnerRequestOptionsService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/workspace/partner-request/options',
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    }, use_e2ee());
};

// ── P1 Workspace — Learning Program requests (School raises → Owner views) ──────

export const createProgramRequestService = (details) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/program-request/create',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { ...details },
    };
    return invokeApi(requestObj);
};

/**
 * Owner workspace → list learning-program requests raised by its schools.
 * For a SCHOOL caller the response also carries `unseen_delivered_total`, which
 * drives the Step 4 delivery badge on the workspace home. Read-only.
 */
/**
 * Owner or school → list learning-program requests.
 *
 * Every filter is applied SERVER-side, so a page of N rows is N matching rows.
 * Empty values are dropped rather than sent as blanks: the API treats an absent
 * param as "no filter", and a literal empty string would otherwise arrive as a
 * filter for the empty value.
 *
 *   tab      open | in_progress | completed | declined  → a set of statuses
 *   status   numeric code (0/1/2/3/5/9)                  → one status
 *   country  ISO-2, resolved server-side to org_codes
 *   source   school_request | suggested
 *   request_type  learning_program (the API's default) | unlock | all
 */
export const listProgramRequestsService = ({ status, status_key, org_code, search, tab, country, solution, source, request_type, page = 1, limit = 10 } = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const query = { page, limit };
    if (status !== undefined && status !== null && status !== '') query.status = status;
    // The Pipeline's Status filter by the chip it shows (completed, ready_to_link…).
    if (status_key) query.status_key = status_key;
    if (org_code) query.org_code = org_code;
    if (search) query.search = search;
    if (tab) query.tab = tab;
    if (country) query.country = country;
    if (solution) query.solution = solution;
    if (source) query.source = source;
    if (request_type) query.request_type = request_type;
    const params = new URLSearchParams(query).toString();
    const requestObj = {
        path: `/dq_insider/dqin/client/program-request/list?${params}`,
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    };
    return invokeApi(requestObj);
};

/**
 * School → its Profile Change Requests queue.
 *
 * Scoped server-side to the caller's own org_code, taken from the token — no
 * org_code is sent, and one would be ignored. A workspace owner has no
 * org_code and is refused: this queue belongs to the school.
 */
export const listProfileChangeRequestsService = ({ status, search, page = 1, limit = 10 } = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const query = { page, limit };
    if (status !== undefined && status !== null && status !== '') query.status = status;
    if (search) query.search = search;
    const params = new URLSearchParams(query).toString();
    return invokeApi({
        path: `/dq_insider/dqin/client/profile-change-request/list?${params}`,
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    });
};

/**
 * School → approve or reject one learner's corrections.
 *
 * Approving is what writes them to the learner's profile; until then the
 * profile is untouched. payload: { request_id, approve: boolean }
 */
export const reviewProfileChangeRequestService = (payload) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/dq_insider/dqin/client/profile-change-request/review',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { ...payload },
    });
};

/**
 * Owner → act on a request: mark it built (delivered) or declined.
 * payload: { request_id, status: 'built' | 'declined', decline_reason? }
 */
/**
 * School → answer a suggestion the partner sent (SW-47).
 *
 * Separate endpoint from update-status: that one is the partner acting on
 * their own workspace, this one is the school acting on a row addressed to
 * it, and the API scopes the two on different fields.
 *
   status  3 accept · 1 request changes · 9 decline
 */
export const respondToSuggestionService = (payload) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/program-request/respond',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: payload,
    };
    return invokeApi(requestObj);
};

export const updateProgramRequestStatusService = (payload) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/program-request/update-status',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { ...payload },
    };
    return invokeApi(requestObj);
};

/**
 * School → acknowledge its deliveries (it opened Step 4). Clears the badge for
 * every delivered-but-unseen program at once, so a batch of deliveries does not
 * re-notify one at a time. Idempotent; scoped server-side to the caller's org.
 */
export const markSchoolDeliveriesSeenService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/program-request/mark-seen',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: {},
    };
    return invokeApi(requestObj);
};

/** Backend ORG_SECTION_DETAILS_TYPE */
const ORG_SECTION_DETAILS_INSTITUTE = 0;
const ORG_SECTION_DETAILS_SECTION = 1;
const DEFAULT_INSTITUTE_CODE = 'default';
const ORG_TYPE_SCHOOL = 3;

/**
 * Random school/institute code — `SCH_` + 8 hex chars from a UUID.
 * Generated client-side when the user creates a NEW school, so the code is
 * stable before the row is posted (classes in the same batch reference it).
 */
const _rand8 = () => {
    const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
    return uuid.replace(/-/g, '').slice(0, 8);
};

export const genSchoolInstituteCode = () => `SCH_${_rand8()}`;

/**
 * Random, unique per-class code — `SEC_` + 8 hex (same shape as the school
 * institute_code). Unlike the class NAME (which can repeat across schools),
 * this is unique, so module-linking / licensing (keyed on section_code) never
 * collide between two schools that both have, e.g., a class "A".
 * The class NAME is still stored in section_name and is what all lookups match on.
 */
export const genSectionCode = () => `SEC_${_rand8()}`;

/**
 * Build the INSTITUTE (school) row for org/sections/post from the dialog's
 * new-school fields. Head country/city/postal are sent as top-level extras —
 * the API's wrapExtraFields moves them into additional_details under the SAME
 * keys the CSV upload path uses (institute_head_*), so both flows stay aligned.
 */
export const buildNewSchoolRow = (school) => ({
    details_type: ORG_SECTION_DETAILS_INSTITUTE,
    org_type: ORG_TYPE_SCHOOL,
    institute_code: school.institute_code,
    institute_name: String(school.schoolName || '').trim(),
    institute_head_name: [school.headFirstName, school.headLastName].filter(Boolean).join(' ').trim(),
    institute_head_email: String(school.headEmail || '').trim(),
    institute_head_country: String(school.headCountry || '').trim(),
    institute_head_city: String(school.headCity || '').trim(),
    institute_head_postal_code: String(school.headPostalCode || '').trim(),
});


export const listOrgClassesService = async (org_code) => {
    const listRes = await listOrgSectionsService(org_code).catch(() => false);
    if (listRes === false) return [];
    const tableData = extractOrgSectionsTableData(listRes);
    return tableData.filter(
        (r) => Number(r.details_type) === ORG_SECTION_DETAILS_SECTION && r.section_code,
    );
};
/**
 * List the org's schools (INSTITUTE rows). The legacy 'default' shell row is
 * excluded — it's an implementation detail, not a user-created school.
 * Rows come back unwrapped, so institute_head_country/city/postal_code are
 * flat fields here (see unwrapExtraFields on the API).
 */
export const listOrgSchoolsService = async (org_code) => {
    const listRes = await listOrgSectionsService(org_code);
    if (listRes === false) {
        throw new Error('Could not load schools (encrypted response). Try logging out and signing in again.');
    }
    const tableData = extractOrgSectionsTableData(listRes);
    return tableData.filter(
        (r) => Number(r.details_type) === ORG_SECTION_DETAILS_INSTITUTE
            && r.institute_code
            && r.institute_code !== DEFAULT_INSTITUTE_CODE,
    );
};

/**
 * Section list/post (insider portal).
 *
 * `opts` is OPTIONAL — omitted, the full dump is returned exactly as before
 * (existing callers are unaffected). With opts the server filters/paginates the
 * SECTION rows (INSTITUTE/school rows always travel in full):
 *   search — class name / grade / teacher name/email / school name filter.
 *   page + limit — 1-based server-side paging, newest class first. A `page`
 *   echo in the response signals the server paginated (older backends ignore
 *   these params and return the full dump — callers client-fallback on that).
 */
export const listOrgSectionsService = async (org_code, opts = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const postData = { org_code };
    /*
     * Which school inside the org. A school inside a network shares its
     * network's org_code, so a network asking for one school's classes has to
     * say WHICH — org_code alone returns every school it owns.
     */
    if (opts.institute_code) postData.institute_code = opts.institute_code;
    if (opts.search) postData.search = String(opts.search).trim();
    if (opts.page) {
        postData.page = Number(opts.page);
        postData.limit = Number(opts.limit) || 10;
    }
    const requestObj = {
        path: '/dq_insider/dqin/client/org/sections/list',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData,
    };
    return invokeApi(requestObj, use_e2ee());
};

/**
 * Bulk create sections.
 *
 * `school` (optional) attaches the new classes to a school (INSTITUTE row):
 *   { institute_code }                — attach to an EXISTING school
 *   { isNew: true, institute_code, schoolName, headEmail, headFirstName,
 *     headLastName, headCountry, headCity, headPostalCode }
 *                                     — create a NEW school in the same post
 *     (institute_code pre-generated via genSchoolInstituteCode()).
 * Omitted → legacy behavior: classes attach to the 'default' shell institute.
 */
export const createOrgClassSectionsService = async (org_code, org_name, classes, school = null) => {
    const listRes = await listOrgSectionsService(org_code);
    if (listRes === false) {
        throw new Error('Could not load organization sections (encrypted response). Try logging out and signing in again.');
    }
    const tableData = extractOrgSectionsTableData(listRes);
    const hasDefaultInstitute = tableData.some(
        (r) => Number(r.details_type) === ORG_SECTION_DETAILS_INSTITUTE
            && r.institute_code === DEFAULT_INSTITUTE_CODE,
    );

    /*
     * Which school the classes belong to — or NOTHING, and let the server say.
     *
     * This used to fall back to 'default'. Add a Class no longer asks which
     * school (buildSchoolSelection always returns null), so EVERY class was
     * stamped 'default' — including one created by a school inside a network,
     * whose own list filters to its real institute_code. The class saved, then
     * vanished. A network never noticed: it owns the code, so its list is
     * unfiltered and shows everything.
     *
     * Sending nothing lets the server resolve it: the caller's own institute
     * when they are a school, and OrgInstitute.ResolveCode otherwise — which
     * still answers 'default' for older orgs, so nothing about them changes.
     * ResolveCode short-circuits on a provided value, so passing one here was
     * precisely what stopped it doing its job.
     */
    const classInstituteCode = school?.institute_code || null;

    const data = [];
    if (school?.isNew && school.institute_code) {
        // New school → INSTITUTE row travels in the same post, before the classes.
        data.push(buildNewSchoolRow(school));
    } else if (!school && !hasDefaultInstitute) {
        // Legacy shell institute — only when no school flow is in play.
        data.push({
            details_type: ORG_SECTION_DETAILS_INSTITUTE,
            institute_code: DEFAULT_INSTITUTE_CODE,
            institute_name: org_name || 'Institute',
        });
    }

    const existingNames = new Set(
        tableData
            .filter(isOrgSectionRow)
            .map((r) => String(r.section_name || r.section_code || '').trim().toLowerCase())
            .filter(Boolean),
    );
    const batchNames = new Set();

    for (const c of classes) {
        const section_name = String(c.className || c.section_name || '').trim();
        if (!section_name) continue;
        const nameKey = section_name.toLowerCase();
        if (existingNames.has(nameKey)) {
            throw new Error(`Class "${section_name}" already exists.`);
        }
        if (batchNames.has(nameKey)) {
            throw new Error(`Duplicate class name in this form: "${section_name}".`);
        }
        batchNames.add(nameKey);

        const incharge_name = [c.teacherFirstName, c.teacherLastName].filter(Boolean).join(' ').trim()
            || String(c.section_incharge_name || '').trim();
        const incharge_email = String(c.teacherEmail || c.section_incharge_email || '').trim();
        const section_level = String(c.gradeLevel || c.section_level || '').trim();
        data.push({
            details_type: ORG_SECTION_DETAILS_SECTION,
            // Omitted entirely when unknown — an explicit value overrides the
            // server-side resolution rather than deferring to it.
            ...(classInstituteCode ? { institute_code: classInstituteCode } : {}),
            // Random unique code (SEC_xxxx) — NOT the class name — so the class is
            // uniquely keyed for module-linking / licensing across schools.
            section_code: genSectionCode(),
            section_name,
            section_level,
            // The class's "Academic Year" (e.g. 2026-2027). Left off when it was
            // not filled in, so nothing is written for it.
            ...(String(c.academicYear || '').trim() ? { academic_year: String(c.academicYear).trim() } : {}),
            section_incharge_name: incharge_name,
            section_incharge_email: incharge_email,
            org_type: ORG_TYPE_SCHOOL,
            // Persisted class prefs (stored server-side in additional_details and
            // returned unwrapped by the sections list) — the enroll dialog
            // prefills these on reopen.
            username_prefix: String(c.userPrefix || c.username_prefix || '').trim(),
            school_postal_code: String(c.schoolPostalCode || c.school_postal_code || '').trim(),
        });
    }

    const sectionRows = data.filter((d) => d.details_type === ORG_SECTION_DETAILS_SECTION);
    if (sectionRows.length === 0) {
        throw new Error('Enter a class name on at least one tab.');
    }

    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const requestObj = {
        path: '/dq_insider/dqin/client/org/sections/post',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: { org_code, data },
    };
    const res = await invokeApi(requestObj, use_e2ee());
    if (res === false) {
        throw new Error('Could not create classes (encrypted response). Try logging out and signing in again.');
    }
    return res;
};

/**
 * Update ONE existing class's stored details (username prefix, school postal
 * code, teacher, grade). Posts a single section row to the same endpoint as
 * class creation — the API's normalize path updates the existing section
 * in place, so no "already exists" pre-check is wanted here.
 */
export const updateOrgClassSectionService = async (org_code, c, school = null) => {
    const section_name = String(c.className || '').trim();
    if (!section_name) throw new Error('Class name is required.');
    // School attach on edit: existing school's code, a NEW school (row posted in
    // the same call), or the class's current code — legacy rows fall back to 'default'.
    const institute_code = school?.institute_code
        || String(c.instituteCode || '').trim()
        || DEFAULT_INSTITUTE_CODE;
    const row = {
        details_type: ORG_SECTION_DETAILS_SECTION,
        institute_code,
        section_code: String(c.sectionCode || '').trim() || section_name,
        section_name,
        section_level: String(c.gradeLevel || '').trim(),
        section_incharge_name: [c.teacherFirstName, c.teacherLastName].filter(Boolean).join(' ').trim(),
        section_incharge_email: String(c.teacherEmail || '').trim(),
        // Only when the caller edits the year (Edit class): left off, the API
        // keeps the stored one, so the other callers of this service never clear it.
        ...(c.academicYear !== undefined ? { academic_year: String(c.academicYear || '').trim() } : {}),
        org_type: ORG_TYPE_SCHOOL,
        username_prefix: String(c.userPrefix || '').trim(),
        school_postal_code: String(c.schoolPostalCode || '').trim(),
    };
    // School switch: tell the API where the class currently lives so it MOVES
    // the section instead of creating a duplicate under the target school.
    const currentCode = String(c.instituteCode || '').trim() || DEFAULT_INSTITUTE_CODE;
    if (school?.institute_code && school.institute_code !== currentCode) {
        row.move_from_institute_code = currentCode;
    }
    const data = (school?.isNew && school.institute_code) ? [buildNewSchoolRow(school), row] : [row];
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/org/sections/post',
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            postData: { org_code, data },
        },
        use_e2ee(),
    );
};

export const linkSectionAssessmentService = async (org_code, section_code, dq_module_codes) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/section/link_assessment',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, section_code, dq_module_codes },
        },
        use_e2ee(),
    );
};

export const getOrgDetailsService = async (org_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: `/dq_insider/dqin/client/org/${org_code}`,
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
        },
        use_e2ee(),
    );
};

/**
 * The org's modules. `institute_code` narrows a network's view to what it made
 * available to one of its schools; a school's own login is narrowed server-side.
 */
export const getOrgModulesService = async ({ org_code, module_type, page = 1, limit = 20, institute_code = null } = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const params = new URLSearchParams({ org_code, page, limit });
    if (module_type) params.set('module_type', module_type);
    if (institute_code) params.set('institute_code', institute_code);
    const requestObj = {
        path: `/dq_insider/dqin/client/org/modules?${params.toString()}`,
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
    };
    return invokeApi(requestObj, use_e2ee());
};

export const getSectionLinkedModulesService = async (org_code, section_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const q = new URLSearchParams({
        org_code,
        section_code,
        _: String(Date.now()),
    }).toString();
    return invokeApi(
        {
            path: `/dq_insider/dqin/client/section/linked_modules?${q}`,
            method: 'GET',
            headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        },
        use_e2ee(),
    );
};

 //current license sub-pool for one class. */
export const getSectionLicensesService = async (org_code, section_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const q = new URLSearchParams({
        org_code,
        section_code,
        _: String(Date.now()),
    }).toString();
    return invokeApi(
        {
            path: `/dq_insider/dqin/client/section/licenses?${q}`,
            method: 'GET',
            headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        },
        use_e2ee(),
    );
};

/**
 * Learner roster for a single class/section (Existing Students tab).
 * opts (all optional — omitted = original full-roster call):
 *   page/limit — server-side pagination; search — name/email filter;
 *   with_meta — include roster_meta (usernames + latest postal) for the
 *   username-prefix derivation, fetched once on first load.
 */
export const getSectionStudentsService = async (org_code, section_code, opts = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const params = {
        org_code,
        section_code,
        _: String(Date.now()),
    };
    if (opts.page) params.page = String(opts.page);
    if (opts.limit) params.limit = String(opts.limit);
    if (opts.search) params.search = String(opts.search);
    if (opts.with_meta) params.with_meta = '1';
    const q = new URLSearchParams(params).toString();
    return invokeApi(
        {
            path: `/dq_insider/dqin/client/section/students?${q}`,
            method: 'GET',
            headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        },
        use_e2ee(),
    );
};

/**
 * Correct one learner's details in one class.
 *
 * Edit only — students cannot be removed once entered, and their class is not
 * editable here (moving a learner changes who has been billed for them). The
 * learner is addressed by `username`, which is itself not editable: it is their
 * sign-in identity, already printed on whatever the school handed out.
 *
 * Allowed before AND after the class is published.
 */
export const updateSectionStudentService = async (org_code, section_code, username, changes = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/section/students/update',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, section_code, username, ...changes },
        },
        use_e2ee(),
    );
};

export const getOrgLinkedModulesService = async (org_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const q = new URLSearchParams({ org_code, _: String(Date.now()) }).toString();
    return invokeApi(
        {
            path: `/dq_insider/dqin/client/org/linked_modules?${q}`,
            method: 'GET',
            headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
        },
        use_e2ee(),
    );
};

export const setModuleVisibilityService = async ({ org_code, module_code, scope, section_codes }) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const postData = { org_code, module_code, scope };
    if (Array.isArray(section_codes) && section_codes.length) {
        postData.section_codes = section_codes;
    }
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/set_module_visibility',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData,
        },
        use_e2ee(),
    );
};
/**
 * Bulk upload of schools / school networks from the Excel template — its own
 * endpoints (the rows are created server-side by the same service functions
 * the Add a School / Add a Network forms use).
 *
 * Start: returns { job_id, total_rows } at once; the rows are created in the
 * background. `payload` is { networks: [{ ...network, schools: [...] }], schools: [...] }.
 */
export const startClientBulkUploadService = async (payload) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/bulk_upload/start',
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            postData: { networks: payload?.networks || [], schools: payload?.schools || [] },
        },
        use_e2ee(),
    );
};

/**
 * Poll a bulk upload: { status, total_rows, processed_rows, counts, results }.
 * Without a job_id, the workspace's upload that is still running (or null).
 */
export const clientBulkUploadStatusService = async (job_id = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/bulk_upload/status',
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            postData: job_id ? { job_id } : {},
        },
        use_e2ee(),
    );
};
